import { useRef, useState } from 'react';

import { Box, SxProps, Theme, Tooltip, Typography } from '@mui/material';

import { DynamicSignatureFormQuestion } from '../../types/taskForm';
import { Checkbox } from '../uiComponents/ComponentLibrary/Checkbox';

interface DynamicSignatureQuestionProps {
  question: DynamicSignatureFormQuestion;
  isSelected: boolean;
  onToggle: (questionId: number) => void;
  sx?: SxProps<Theme>;
}

const customStyles: Record<string, SxProps<Theme>> = {
  questionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 1,
    border: (theme) => `1px solid ${theme.palette.divider}`,
    borderRadius: '12px',
    padding: '16px',
    backgroundColor: '#FFF',
    cursor: 'pointer',
    '&:hover': {
      borderColor: (theme) => theme.palette.primary.main,
      borderWidth: '1px',
    },
  },
  questionItemSelected: {
    borderColor: (theme) => theme.palette.primary.main,
    backgroundColor: (theme) => theme.palette.common.white,
    boxShadow: '0 2px 4px rgba(25, 118, 210, 0.1)',
  },
  checkbox: {
    padding: '8px',
    '& .MuiSvgIcon-root': {
      fontSize: '16px',
    },
  },
  questionText: {
    fontSize: '14px',
    color: '#000000',
    fontWeight: 400,
    lineHeight: '22px',
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    wordBreak: 'break-word',
  },
};

export const DynamicSignatureQuestion = ({
  question,
  isSelected,
  onToggle,
  sx,
}: DynamicSignatureQuestionProps) => {
  const textRef = useRef<HTMLDivElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  const checkOverflow = () => {
    if (textRef.current) {
      const isOverflowing = textRef.current.scrollHeight > textRef.current.clientHeight;
      setShowTooltip(isOverflowing);
    }
  };

  return (
    <Box
      sx={
        {
          ...customStyles.questionItem,
          ...(isSelected ? customStyles.questionItemSelected : {}),
          ...sx,
        } as SxProps<Theme>
      }
      onClick={() => onToggle(question.id)}
    >
      <Checkbox
        checked={isSelected}
        sx={customStyles.checkbox}
      />
      <Tooltip
        title={showTooltip ? question.title : ''}
        placement='bottom'
      >
        <Typography
          ref={textRef}
          sx={customStyles.questionText}
          onMouseEnter={checkOverflow}
        >
          {question.title}
        </Typography>
      </Tooltip>
    </Box>
  );
};
